import {
    Typography,
    DialogContent,
    Button,
    Grid,
    useTheme,
    Stack,
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    alpha,
} from "@mui/material";
import { Fragment, useState } from "react";
import { DevelopmentItem, NoteAssociationType, NoteDisplay, OngoingAssessment } from '../models/dtos';
import { useErrorDisplay } from "../components/ErrorDisplay"
import { ExtractForSetError, IsEmpty, IsNotNullOrUndefined, IsNullOrUndefined } from "../Helpers";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { UpsertAction } from "../Enums";
import DisplayDialogContent from "../ui-components/DisplayDialogContent";
// Icons
import PeopleIcon from '@mui/icons-material/People';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AddTaskIcon from '@mui/icons-material/AddTask';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import NotesIcon from '@mui/icons-material/Notes';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MenuOpenRounded from '@mui/icons-material/MenuOpenRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
// Components
import TalentEventManagementComponent from "../ui-components/TalentEventManagementComponent";
import OngoingAssessmentComponent from "../ui-components/OngoingAssessmentComponent";
import TalentEditorContent from "../ui-components/TalentEditorContent";
import ConditionalDisplay from "../components/ConditionalDisplay";
import NoteUpsertComponent from "../ui-components/NoteUpsertComponent";
import NoteListComponent from "../ui-components/NoteListComponent";
import NoteUpsertPopup from "./NoteUpsertPopup";
import DevelopmentItemUpsertPopup from "./DevelopmentItemUpsertPopup";
import DevelopmentItemListDisplayComponent from "../ui-components/DevelopmentItemListDisplayComponent";
import { I18N } from "../I18N";
import TalentClient from "../apiClients/TalentClient";
import DevelopmentClient, { DevelopmentItemErrors } from "../apiClients/DevelopmentClient";
import NoteClient, { NoteErrors } from "../apiClients/NoteClient";
import AssessmentListComponent from "../ui-components/AssessmentListComponent";
import { useAccessInfo } from "../contexts/AccessTokenProvider";
import useViewportMode from "../hooks/useViewportMode";
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";

export interface TalentManagementPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    talentFullName: string;
    talentId: string;
    onChange?: () => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    onClose?: () => void;
    icon?: JSX.Element;
    initialTab?: TalentManagementTab;
}

export type TalentManagementTab =
    | "None"
    | "EditInfo"
    | "EventManagement"
    | "OngoingAssessmentEditor"
    | "Notes"
    | "DevelopmentPlan"
    | "Assessments";

const TalentManagementPopup = ({ mode, talentFullName, talentId, onChange = async () => { }, onAdd = async () => { }, onClose = () => { }, icon = <PeopleIcon />, initialTab = "EditInfo", ...btnProps }: TalentManagementPopupProps) => {

    const devPlanCreateTooltip = "Create Development Item";
    const devPlanCreateDisabledTooltip = "Can only create a Development Plan with an active Template.";

    const usePopups = false;

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const access = useAccessInfo();
    const subscriptionActive = access.hasSubscriptionAccess;
    const { isPhone, isTablet, isDesktop, isPortrait } = useViewportMode();
    const useCompactSectionNavigation = isPhone || (isTablet && isPortrait);
    const [sectionNavigationCollapsed, setSectionNavigationCollapsed] = useState(isDesktop);
    const useDesktopIconRail = isDesktop && sectionNavigationCollapsed;

    const [listItemSelected, setIistItemSelected] = useState<MenuItem>(0);
    const [developmentItems, setDevelopmentItems] = useState<DevelopmentItem[]>([]);
    const [enableAddDevPlanItem, setEnableAddDevPlanItem] = useState<boolean>(true);
    const [notes, setNotes] = useState<NoteDisplay[]>([]);

    const title = (IsNotNullOrUndefined(talentFullName)) ? `Dashboard: ${talentFullName}` : "Manage Talent";
    const [hoverContent, setHoverContent] = useState<JSX.Element | string>(title);

    enum MenuItem {
        None, EditInfo, EventManagement, OngoingAssessmentEditor, Notes, DevelopmentPlan, Assessments
    }

    const sectionNeedsWorkspace = listItemSelected === MenuItem.OngoingAssessmentEditor;
    const fillDialogViewport = useCompactSectionNavigation || sectionNeedsWorkspace;

    const mapInitialTab = (tab: TalentManagementTab): MenuItem => {
        switch (tab) {
            case "EditInfo":
                return MenuItem.EditInfo;
            case "EventManagement":
                return MenuItem.EventManagement;
            case "OngoingAssessmentEditor":
                return MenuItem.OngoingAssessmentEditor;
            case "Notes":
                return MenuItem.Notes;
            case "DevelopmentPlan":
                return MenuItem.DevelopmentPlan;
            case "Assessments":
                return MenuItem.Assessments;
            default:
                return MenuItem.None;
        }
    };

    const handleOpen = () => {
        setHoverContent(title);

        setIistItemSelected(mapInitialTab(initialTab));
        setOpen(true);

    };

    const handleListItemClick = async (e, i: MenuItem) => {
        setIistItemSelected(i);

        if (i === MenuItem.DevelopmentPlan && subscriptionActive) {
            await loadDevelopmentPlan();
        }
        else if (i === MenuItem.Notes) {
            await loadNotes();
        }

    };

    const handleClose = () => {
        setOpen(false);
        onClose();
    };

    const loadDevelopmentPlan = async () => {
        try {
            setBusy(true);

            // TODO-RGK:[Refactor] Move these to Development controller and setup a new endpoint to feed just/all the required data
            const apiResponse = await new DevelopmentClient().GetDevelopmentItems(talentId);
            if(ExtractForSetError(apiResponse, DevelopmentItemErrors.GetSingle, setError))
            return;

            const di = apiResponse.Content;
            setDevelopmentItems(di);

            // respect the dev plan max org settings
            const activeItems = di.filter(f => IsEmpty(f.CompleteDate));
            let enableAdd = activeItems.length <= access.accessInfo.selectedOrganizationSettings.DevelopmentPlanMax;

            // can only add if a template is active
            // Get the Ongoing Assessment Graph for the Talent
            let oa: OngoingAssessment;
            if (enableAdd) {
                const apiResponse = await new TalentClient().GetOngoingAssessment(talentId);
                if (ExtractForSetError(apiResponse, DevelopmentItemErrors.GetSingle, setError))
                    return;

                oa = apiResponse.Content;

                // TODO also need to check if there have been an assessment published so we can disable the button
                // ie set enableAdd = false;
            }

            if (IsNullOrUndefined(oa))
                enableAdd = false;

            setEnableAddDevPlanItem(enableAdd);

        }
        catch (error) {
            setError(error, DevelopmentItemErrors.GetAll);
        }
        finally {
            setBusy(false);
        }
    }

    const loadNotes = async () => {
        try {
            setBusy(true);

            let n = await new NoteClient().GetAll(talentId);
            setNotes(n);
        }
        catch (error) {
            setError(error, NoteErrors.GetAll);
        }
        finally {
            setBusy(false);
        }
    }

    return (
        <>
            <ErrorDisplay />
            <IconButtonEx disabled={busy} hoverContent={hoverContent} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

            <ResponsiveDialog
                aria-labelledby="customized-dialog-title"
                open={open}
                fullWidth
                maxWidth={false}
                tabletFullScreen="portrait"
                widthTier={sectionNeedsWorkspace ? 'wide' : listItemSelected === MenuItem.EditInfo ? 'compact' : 'comfortable'}
                sizingStrategy="workspace"
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        // Dense assessment tooling receives a bounded workspace. Simpler
                        // sections remain content-driven instead of inheriting a huge,
                        // mostly empty fixed-height canvas.
                        height: fillDialogViewport ? 'calc(100% - 64px)' : 'auto',
                        maxHeight: 'calc(100% - 64px)',
                        minHeight: 0,
                        overflow: 'hidden',
                    },
                }}
            >
                <ResponsiveDialogHeader id="customized-dialog-title" title={title} onClose={handleClose} />
                <DialogContent
                    dividers
                    sx={{
                        marginTop: isDesktop ? theme.spacing(1) : 0,
                        padding: isDesktop ? theme.spacing(2) : 0,
                        flex: fillDialogViewport ? '1 1 auto' : '0 1 auto',
                        minHeight: 0,
                        overflow: fillDialogViewport ? 'hidden' : 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        data-testid="talent-management-workspace"
                        data-layout={useCompactSectionNavigation ? 'compact' : isTablet ? 'tablet-landscape' : 'desktop'}
                        sx={{
                            flex: fillDialogViewport ? '1 1 auto' : '0 1 auto',
                            height: fillDialogViewport ? '100%' : 'auto',
                            minHeight: 0,
                            width: '100%',
                            overflow: fillDialogViewport ? 'hidden' : 'visible',
                        }}
                    >
                        <BusySpinner busy={busy}>
                            {/* Master/Detail Display Scheme */}
                            <ConditionalDisplay ifTrue={!usePopups}>
                                <Grid container sx={{ height: fillDialogViewport ? '100%' : 'auto', minHeight: 0, overflow: fillDialogViewport ? 'hidden' : 'visible' }}>
                                    {/* Menu */}
                                    <Grid
                                        item
                                        xs={useCompactSectionNavigation ? 12 : useDesktopIconRail ? 1 : 3}
                                        sx={{
                                            minWidth: 0,
                                            height: useCompactSectionNavigation || !fillDialogViewport ? 'auto' : '100%',
                                            borderRight: useCompactSectionNavigation ? 0 : 1,
                                            borderBottom: useCompactSectionNavigation ? 1 : 0,
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Box sx={{ width: '100%', maxWidth: useCompactSectionNavigation ? 'none' : useDesktopIconRail ? 80 : 360, bgcolor: 'background.paper', height: fillDialogViewport ? '100%' : 'auto' }}>
                                            <List
                                                component="nav"
                                                aria-label="Talent management sections"
                                                sx={{
                                                    display: useCompactSectionNavigation ? 'flex' : 'block',
                                                    overflowX: useCompactSectionNavigation ? 'auto' : 'visible',
                                                    overflowY: useCompactSectionNavigation ? 'hidden' : 'auto',
                                                    p: useCompactSectionNavigation ? 0.5 : 0,
                                                    gap: useCompactSectionNavigation ? 0.5 : 0,
                                                    whiteSpace: useCompactSectionNavigation ? 'nowrap' : 'normal',
                                                    scrollbarWidth: 'thin',
                                                    ...(useDesktopIconRail && {
                                                        '& .MuiListItemText-root': { display: 'none' },
                                                        '& .MuiListItemIcon-root': { minWidth: 0, justifyContent: 'center' },
                                                        '& .MuiListItemButton-root': { justifyContent: 'center', px: 1 },
                                                    }),
                                                }}
                                            >
                                                {isDesktop && (
                                                    <ListItemButton
                                                        aria-label={useDesktopIconRail ? 'Expand Talent management navigation' : 'Collapse Talent management navigation'}
                                                        title={useDesktopIconRail ? 'Expand navigation' : 'Collapse navigation'}
                                                        onClick={() => setSectionNavigationCollapsed(value => !value)}
                                                        sx={{ minHeight: 48 }}
                                                    >
                                                        <ListItemIcon>{useDesktopIconRail ? <MenuRounded /> : <MenuOpenRounded />}</ListItemIcon>
                                                        <ListItemText primary={useDesktopIconRail ? 'Expand navigation' : 'Collapse navigation'} />
                                                    </ListItemButton>
                                                )}
                                                <ListItemButton
                                                    aria-label="Ongoing Assessment"
                                                    title={useDesktopIconRail ? 'Ongoing Assessment' : undefined}
                                                    selected={listItemSelected === MenuItem.OngoingAssessmentEditor}
                                                    onClick={(event) => handleListItemClick(event, MenuItem.OngoingAssessmentEditor)}
                                                    sx={{ minHeight: 48, flex: useCompactSectionNavigation ? '0 0 auto' : undefined, borderRadius: useCompactSectionNavigation ? 1 : 0 }}>
                                                    <ListItemIcon>
                                                        <DeveloperBoardIcon />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Ongoing Assessment" />
                                                </ListItemButton>
                                                <ListItemButton
                                                    aria-label="Development Plan"
                                                    title={useDesktopIconRail ? 'Development Plan' : undefined}
                                                    selected={listItemSelected === MenuItem.DevelopmentPlan}
                                                    onClick={(event) => handleListItemClick(event, MenuItem.DevelopmentPlan)}
                                                    sx={{ minHeight: 48, flex: useCompactSectionNavigation ? '0 0 auto' : undefined, borderRadius: useCompactSectionNavigation ? 1 : 0 }}>
                                                    <ListItemIcon>
                                                        <DashboardIcon />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Development Plan" />
                                                    <ConditionalDisplay ifTrue={subscriptionActive}>
                                                        <Tooltip placement="top-end" title={(!enableAddDevPlanItem) ? devPlanCreateDisabledTooltip : ''}>
                                                            <span>
                                                                <DevelopmentItemUpsertPopup
                                                                    disabled={!enableAddDevPlanItem}
                                                                    mode={UpsertAction.Create}
                                                                    talentId={talentId}
                                                                    onAdd={(id) => loadDevelopmentPlan()}
                                                                    icon={<DashboardCustomizeIcon />}
                                                                    hoverContent={(enableAddDevPlanItem) ? devPlanCreateTooltip : ''} />
                                                            </span>
                                                        </Tooltip>
                                                    </ConditionalDisplay>
                                                    <ConditionalDisplay ifTrue={!subscriptionActive}>
                                                        <Tooltip placement="top-end" title="Subscription required">
                                                            <span>
                                                                {/* TODO use theme palette for disabled color */}
                                                                <DashboardCustomizeIcon sx={{ color: alpha('#919EAB', 0.8) }} />
                                                            </span>
                                                        </Tooltip>
                                                    </ConditionalDisplay>
                                                </ListItemButton>
                                                <ListItemButton
                                                    aria-label="Notes"
                                                    title={useDesktopIconRail ? 'Notes' : undefined}
                                                    selected={listItemSelected === MenuItem.Notes}
                                                    onClick={(event) => handleListItemClick(event, MenuItem.Notes)}
                                                    sx={{ minHeight: 48, flex: useCompactSectionNavigation ? '0 0 auto' : undefined, borderRadius: useCompactSectionNavigation ? 1 : 0 }}>
                                                    <ListItemIcon>
                                                        <NotesIcon />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Notes" />
                                                    <Tooltip placement="top-end" title={(subscriptionActive) ? "" : "Subscription required"}>
                                                        <span>
                                                            <NoteUpsertPopup disabled={!subscriptionActive} mode={UpsertAction.Create} associationId={talentId} associationType={NoteAssociationType.Talent} onAdd={loadNotes} hoverContent={I18N.Translate('Add Note')} visibleLabel={I18N.Translate('Add')} aria-label={I18N.Translate('Add Note')} />
                                                        </span>
                                                    </Tooltip>
                                                </ListItemButton>
                                                <ListItemButton
                                                    aria-label="Events and Challenges"
                                                    title={useDesktopIconRail ? 'Events/Challenges' : undefined}
                                                    selected={listItemSelected === MenuItem.EventManagement}
                                                    onClick={(event) => handleListItemClick(event, MenuItem.EventManagement)}
                                                    sx={{ minHeight: 48, flex: useCompactSectionNavigation ? '0 0 auto' : undefined, borderRadius: useCompactSectionNavigation ? 1 : 0 }}>
                                                    <ListItemIcon>
                                                        <ManageAccountsIcon />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Events/Challenges" />
                                                </ListItemButton>
                                                <ListItemButton
                                                    aria-label="Assessments"
                                                    title={useDesktopIconRail ? 'Assessments' : undefined}
                                                    selected={listItemSelected === MenuItem.Assessments}
                                                    onClick={(event) => handleListItemClick(event, MenuItem.Assessments)}
                                                    sx={{ minHeight: 48, flex: useCompactSectionNavigation ? '0 0 auto' : undefined, borderRadius: useCompactSectionNavigation ? 1 : 0 }}>
                                                    <ListItemIcon>
                                                        <AssessmentIcon />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Assessments" />
                                                </ListItemButton>
                                                <ListItemButton
                                                    aria-label="General"
                                                    title={useDesktopIconRail ? 'General' : undefined}
                                                    selected={listItemSelected === MenuItem.EditInfo}
                                                    onClick={(event) => handleListItemClick(event, MenuItem.EditInfo)}
                                                    sx={{ minHeight: 48, flex: useCompactSectionNavigation ? '0 0 auto' : undefined, borderRadius: useCompactSectionNavigation ? 1 : 0 }}>
                                                    <ListItemIcon>
                                                        <PeopleIcon />
                                                    </ListItemIcon>
                                                    <ListItemText primary="General" />
                                                </ListItemButton>
                                            </List>
                                        </Box>
                                    </Grid>
                                    {/* Display Editor Control */}
                                    <Grid item xs={useCompactSectionNavigation ? 12 : useDesktopIconRail ? 11 : 9} sx={{ minWidth: 0, minHeight: 0, height: fillDialogViewport ? (useCompactSectionNavigation ? 'calc(100% - 57px)' : '100%') : 'auto' }}>
                                        <Box
                                            role="region"
                                            aria-label="Selected talent management section"
                                            sx={{
                                                width: '100%',
                                                height: fillDialogViewport ? '100%' : 'auto',
                                                minHeight: 0,
                                                bgcolor: 'background.paper',
                                                overflowY: listItemSelected === MenuItem.OngoingAssessmentEditor ? 'hidden' : 'auto',
                                                overscrollBehavior: 'contain',
                                                p: { xs: 0, sm: 1, lg: 0 },
                                            }}
                                        >
                                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.OngoingAssessmentEditor}>
                                                <OngoingAssessmentComponent
                                                    subscriptionActive={subscriptionActive}
                                                    talentId={talentId}
                                                    heightDetractor={450}
                                                    fillAvailableHeight
                                                />
                                            </ConditionalDisplay>
                                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.DevelopmentPlan}>
                                                <DevelopmentItemListDisplayComponent subscriptionActive={subscriptionActive} talentId={talentId} data={developmentItems} onChange={loadDevelopmentPlan} />
                                            </ConditionalDisplay>
                                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.Notes}>
                                                <NoteListComponent subscriptionActive={subscriptionActive} associationId={talentId} data={notes} onChange={loadNotes} />
                                            </ConditionalDisplay>
                                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.EventManagement}>
                                                <TalentEventManagementComponent subscriptionActive={subscriptionActive} talentId={talentId} />
                                            </ConditionalDisplay>
                                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.Assessments}>
                                                <AssessmentListComponent subscriptionActive={subscriptionActive} talentId={talentId} />
                                            </ConditionalDisplay>
                                            <ConditionalDisplay ifTrue={listItemSelected === MenuItem.EditInfo}>
                                                <TalentEditorContent talentId={talentId} onClose={onChange} />
                                            </ConditionalDisplay>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </ConditionalDisplay>

                            {/* Tiles that use Display Dialog Content to Popup the Editor screens */}
                            <ConditionalDisplay ifTrue={usePopups}>
                                <Grid container spacing={3}>
                                    <Grid item xs={3}>
                                        <Stack direction="row">
                                            <DisplayDialogContent
                                                title={`Add Note: ${talentFullName}`}
                                                mode={UpsertAction.Create}
                                                content={
                                                    <NoteUpsertComponent
                                                        mode={UpsertAction.Create}
                                                        associationId={talentId}
                                                        associationType={NoteAssociationType.Talent} />
                                                }
                                                busy={false}
                                                icon={<ManageAccountsIcon />} />
                                            <Typography sx={{ fontSize: 'large', alignSelf: 'center' }}>Talent Data</Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Stack direction="row">
                                            <DisplayDialogContent
                                                title={`Manage Talent: ${talentFullName}`}
                                                mode={UpsertAction.Update}
                                                content={<TalentEditorContent talentId={talentId} onClose={onChange} />}
                                                busy={false}
                                                icon={<ManageAccountsIcon />} />
                                            <Typography sx={{ fontSize: 'large', alignSelf: 'center' }}>Talent Data</Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Box sx={{ border: '1em', borderColor: 'theme.palette.secondary' }}>
                                            <Stack direction="row">
                                                <DisplayDialogContent
                                                    sx={{ alignSelf: 'top' }}
                                                    title={`Manage Events: ${talentFullName}`}
                                                    mode={UpsertAction.Update}
                                                    content={<TalentEventManagementComponent subscriptionActive={subscriptionActive} talentId={talentId} />}
                                                    busy={false}
                                                    icon={<AddTaskIcon />} />
                                                <Typography sx={{ fontSize: 'large', alignSelf: 'center' }}>Event Management</Typography>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </ConditionalDisplay>
                        </BusySpinner>
                    </Box>
                </DialogContent>
                <StickyDialogActions viewportOverride={isPhone ? 'phone' : isTablet ? 'tablet' : 'desktop'} phoneLayout="inline">
                    <Button onClick={handleClose} variant='text' color='inherit'>Close</Button>
                </StickyDialogActions>
            </ResponsiveDialog>
        </>)
}

export default TalentManagementPopup;
